import { Modal, message } from "antd";
import WeightageInput from "./WeightageInput";
import COLORS from "common/Colors";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

export const DEFAULT_SCORE_WEIGHTAGE = { technical: 80, soft: 20 };

// Overall Average of a closed appraisal:
// ((technical% * technical average) + (soft% * soft skill average)) / 100.
// Returns null until both averages exist.
export const weightedAverage = (technicalAverage, softAverage, weightage) => {
  if (typeof technicalAverage !== "number" || typeof softAverage !== "number") return null;
  const { technical, soft } = weightage || DEFAULT_SCORE_WEIGHTAGE;
  return (technical * technicalAverage + soft * softAverage) / 100;
};

export const fetchScoreWeightage = async () => {
  try {
    const { code, data } = await CALL_API("performance-kpi-management/settings/weightage", "get");
    const technical = Number(data?.technical);
    const soft = Number(data?.soft);
    // Anything unexpected (e.g. the endpoint isn't deployed yet and answers
    // "Invalid resource") falls back to the default split.
    if (code === STATUS_CODE.SUCCESS && Number.isFinite(technical) && technical + soft === 100) {
      return { technical, soft };
    }
  } catch (error) {
    console.error("Error fetching score weightage:", error);
  }
  return DEFAULT_SCORE_WEIGHTAGE;
};

const Wrap = styled.div`
  .sub-text {
    color: ${COLORS.TEXT.PRIMARY};
    margin-bottom: 20px;
  }
  .weight-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid #f0f0f0;
  }
  .weight-label {
    font-weight: 500;
  }
  .total {
    display: flex;
    justify-content: space-between;
    padding-top: 12px;
    font-weight: 600;
  }
  .error-text {
    color: ${COLORS.ERROR};
    font-size: 13px;
    margin-top: 6px;
    text-align: right;
  }
  .formula {
    background: #f5f7fd;
    border-radius: 8px;
    padding: 12px 14px;
    margin-top: 20px;
    font-size: 13px;
    color: ${COLORS.TEXT.PRIMARY};
  }
  .formula b {
    color: ${COLORS.SECONDARY_BLACK};
  }
`;

const isPercent = (value) => typeof value === "number" && value >= 0 && value <= 100;

// Performance > Settings: company-wide split between Technical Skills and
// Soft Skills used for the Overall Average in Appraisal > My Team. Both are
// entered separately and must add up to exactly 100 before Save is allowed.
const WeightageSettingsModal = ({ open, onClose, weightage, onSaved, canEdit }) => {
  const saved = weightage || DEFAULT_SCORE_WEIGHTAGE;
  const [technical, setTechnical] = useState(saved.technical);
  const [soft, setSoft] = useState(saved.soft);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTechnical(saved.technical);
    setSoft(saved.soft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, weightage]);

  const bothEntered = isPercent(technical) && isPercent(soft);
  const total = (Number(technical) || 0) + (Number(soft) || 0);
  const validationError = !bothEntered
    ? "Enter both Technical Skills and Soft Skills percentages."
    : total !== 100
    ? `Technical Skills and Soft Skills must add up to 100% (currently ${total}%).`
    : null;
  const isUnchanged = technical === saved.technical && soft === saved.soft;

  const onSave = async () => {
    if (validationError) {
      message.error(validationError);
      return;
    }
    setSaving(true);
    try {
      const { code, data, message: apiMessage } = await CALL_API(
        "performance-kpi-management/settings/weightage",
        "patch",
        { technical, soft }
      );
      // An undeployed handler answers SUCCESS + "Invalid resource" with no
      // data, so require the saved values back.
      if (code === STATUS_CODE.SUCCESS && Number.isFinite(Number(data?.technical))) {
        message.success("Weightage updated");
        onSaved?.({ technical: Number(data.technical), soft: Number(data.soft) });
        onClose();
      } else {
        message.error(
          apiMessage && apiMessage !== "Invalid resource" ? apiMessage : "Failed to update weightage"
        );
      }
    } catch (error) {
      console.error("Error saving score weightage:", error);
      message.error("Failed to update weightage");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Score Weightage Settings"
      open={open}
      onCancel={onClose}
      width={480}
      centered
      footer={
        canEdit
          ? [
              <ButtonOutlined key="cancel" onClick={onClose}>
                Cancel
              </ButtonOutlined>,
              <ButtonFilled
                key="save"
                type="primary"
                loading={saving}
                disabled={isUnchanged || Boolean(validationError)}
                // ButtonFilled keeps its blue fill when disabled - fade it so
                // it doesn't look clickable.
                style={isUnchanged || validationError ? { opacity: 0.5 } : undefined}
                onClick={onSave}
              >
                Save
              </ButtonFilled>,
            ]
          : null
      }
    >
      <Wrap>
        <p className="sub-text">
          Set how much Technical Skills and Soft Skills scores count towards the Overall Average of a
          closed appraisal. The two must add up to 100%.
        </p>

        <div className="weight-row">
          <span className="weight-label">Technical Skills</span>
          {/* Text input that only accepts digits (onKeyDown) and caps at 100 -
              same control the KPI forms use for weightage. */}
          <WeightageInput
            value={technical}
            disabled={!canEdit}
            status={validationError ? "error" : undefined}
            onChange={setTechnical}
            addonAfter="%"
            style={{ width: 130 }}
          />
        </div>
        <div className="weight-row">
          <span className="weight-label">Soft Skills</span>
          <WeightageInput
            value={soft}
            disabled={!canEdit}
            status={validationError ? "error" : undefined}
            onChange={setSoft}
            addonAfter="%"
            style={{ width: 130 }}
          />
        </div>
        <div className="total">
          <span>Total</span>
          <span style={{ color: total === 100 && bothEntered ? COLORS.SUCCESS : COLORS.ERROR }}>
            {total}%
          </span>
        </div>
        {validationError && <div className="error-text">{validationError}</div>}

        <div className="formula">
          Overall Average = ((<b>{isPercent(technical) ? technical : "-"}</b> × Technical average
          score) + (<b>{isPercent(soft) ? soft : "-"}</b> × Soft skill average score)) / 100
        </div>
      </Wrap>
    </Modal>
  );
};

export default WeightageSettingsModal;
